import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Lightbulb, Bookmark, ArrowRight, Rocket } from "lucide-react";
import { toast } from "sonner";
import { DifficultyPill } from "./Badges";
import { LogoBadge } from "./LogoBadge";
import { useNotebook } from "../hooks/useNotebook";
import { usePro } from "../hooks/usePro";

export const InspirationModal = ({ business, onClose, onBuild }) => {
  const navigate = useNavigate();
  const [thoughts, setThoughts] = useState("");
  const { toggleSave, addNote, isSaved } = useNotebook();
  const { attempt } = usePro();

  useEffect(() => {
    setThoughts("");
  }, [business]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSave = () => {
    if (isSaved(business.id)) {
      toast.message("Already in your Startup Notebook");
      onClose();
      return;
    }
    attempt("save", () => {
      toggleSave(business);
      if (thoughts.trim()) addNote({ text: thoughts.trim(), businessName: business.title });
      toast.success("Idea saved to your Startup Notebook", {
        description: thoughts.trim() ? "Your reflection was saved too." : undefined,
      });
      onClose();
    });
  };

  return (
    <AnimatePresence>
      {business && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F0F0F]/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="inspiration-modal-overlay"
        >
          <motion.div
            className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl relative max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="inspiration-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-6 right-6 text-[#687280] hover:text-[#0F0F0F] transition-colors"
              data-testid="modal-close-x"
            >
              <X size={22} />
            </button>

            <div className="p-8 md:p-10">
              {/* header snapshot */}
              <div className="flex items-start gap-5 mb-8">
                <LogoBadge size="w-16 h-16" rounded="rounded-2xl" />
                <div>
                  <h2 className="font-display text-2xl font-bold text-[#0F0F0F]">{business.title}</h2>
                  <p className="text-sm font-medium text-[#687280] mt-0.5">
                    {business.category_name}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mb-8">
                <Row label="Category" value={business.category_name} />
                {business.revenueModel && <Row label="Revenue Model" value={business.revenueModel} />}
                {business.buildTime && <Row label="Build Time" value={business.buildTime} />}
                {business.difficulty && <Row label="Startup Difficulty" node={<DifficultyPill level={business.difficulty} />} />}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="font-display font-semibold text-[#0F0F0F] mb-2">Problem Solved</h4>
                  <p className="text-sm text-[#687280] leading-relaxed">{business.problem}</p>
                </div>
                <div>
                  <h4 className="font-display font-semibold text-[#0F0F0F] mb-2">Core Solution</h4>
                  <p className="text-sm text-[#687280] leading-relaxed">{business.solution}</p>
                </div>
              </div>

              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb size={18} className="text-[#D4AF37]" />
                  <h4 className="font-display font-semibold text-[#0F0F0F]">How could you build a better version, or one for a different audience?</h4>
                </div>
                <textarea
                  value={thoughts}
                  onChange={(e) => setThoughts(e.target.value)}
                  placeholder="Write your thoughts..."
                  className="w-full bg-[#F7F7F7] border-0 rounded-xl p-4 min-h-[110px] focus:ring-2 focus:ring-[#D4AF37] outline-none resize-none text-[#0F0F0F] text-sm placeholder-[#9CA3AF]"
                  data-testid="modal-thoughts-input"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/business/${business.id}`);
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0F0F0F] text-white rounded-full py-3.5 text-sm font-medium hover:bg-[#1c1c1c] transition-colors"
                  data-testid="modal-view-full-btn"
                >
                  View Full Business <ArrowRight size={16} />
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0F0F0F] text-white rounded-full py-3.5 text-sm font-medium hover:bg-[#1c1c1c] transition-colors"
                  data-testid="modal-save-idea-btn"
                >
                  <Bookmark size={16} /> Save Idea
                </button>
                <button
                  onClick={() =>
                    attempt("build", () => {
                      onClose();
                      onBuild && onBuild(business);
                    })
                  }
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors"
                  data-testid="modal-build-business-btn"
                >
                  <Rocket size={16} /> Build This Business
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Row = ({ label, value, node }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-sm text-[#687280]">{label}</span>
    <span className="text-sm font-medium text-[#0F0F0F] text-right">{node || value}</span>
  </div>
);
